/**
 * CSRF Protection Utilities
 * Implements Double Submit Cookie pattern for CSRF protection.
 */

import { analytics } from "../lib/analytics";
import { trace } from "../lib/trace";

const CSRF_TOKEN_KEY = "csrf_token";
const CSRF_SYNCED_AT_KEY = "csrf_token_synced_at";
const CSRF_SYNC_TTL_MS = 55 * 60 * 1000;

let csrfInitializationPromise: Promise<string> | null = null;

/**
 * Generate a secure random CSRF token.
 */
export function generateCsrfToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Store CSRF token in session storage.
 */
export function storeCsrfToken(token: string): void {
  try {
    sessionStorage.setItem(CSRF_TOKEN_KEY, token);
  } catch (error: unknown) {
    analytics.error("csrf_store_token_error", error);
  }
}

function storeCsrfSyncedAt(atMs: number): void {
  try {
    sessionStorage.setItem(CSRF_SYNCED_AT_KEY, String(atMs));
  } catch (error: unknown) {
    analytics.error("csrf_store_sync_error", error);
  }
}

function getCsrfSyncedAt(): number | null {
  try {
    const raw = sessionStorage.getItem(CSRF_SYNCED_AT_KEY);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch (error: unknown) {
    analytics.error("csrf_retrieve_sync_error", error);
    return null;
  }
}

function hasRecentCsrfSync(now = Date.now()): boolean {
  const syncedAt = getCsrfSyncedAt();
  return syncedAt !== null && now - syncedAt <= CSRF_SYNC_TTL_MS;
}

/**
 * Retrieve CSRF token from session storage.
 */
export function getCsrfToken(): string | null {
  try {
    return sessionStorage.getItem(CSRF_TOKEN_KEY);
  } catch (error: unknown) {
    analytics.error("csrf_retrieve_token_error", error);
    return null;
  }
}

/**
 * Clear CSRF token (on logout).
 */
export function clearCsrfToken(): void {
  try {
    sessionStorage.removeItem(CSRF_TOKEN_KEY);
    sessionStorage.removeItem(CSRF_SYNCED_AT_KEY);
  } catch (error: unknown) {
    analytics.error("csrf_clear_token_error", error);
  }
}

async function syncCsrfCookie(token: string): Promise<void> {
  const response = await fetch("/api/auth/csrf", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    throw new Error(`CSRF sync failed (${response.status})`);
  }

  storeCsrfSyncedAt(Date.now());
  trace.auth("CSRF token sent to server");
}

export interface InitializeCsrfOptions {
  forceRefresh?: boolean;
  forceServerSync?: boolean;
}

/**
 * Initialize or refresh the CSRF token and cookie binding.
 */
export async function initializeCsrfToken(options: InitializeCsrfOptions = {}): Promise<string> {
  if (csrfInitializationPromise) return csrfInitializationPromise;

  csrfInitializationPromise = (async () => {
    let token = getCsrfToken();
    const mustRefreshToken = options.forceRefresh || !token;

    if (mustRefreshToken) {
      token = generateCsrfToken();
      storeCsrfToken(token);
      trace.auth("CSRF token generated and stored", { forceRefresh: Boolean(options.forceRefresh) });
    } else {
      trace.auth("CSRF token already exists in session");
    }

    if (!token) {
      token = generateCsrfToken();
      storeCsrfToken(token);
    }

    const shouldSyncServer = mustRefreshToken || options.forceServerSync || !hasRecentCsrfSync();

    if (shouldSyncServer) {
      try {
        await syncCsrfCookie(token);
      } catch (error: unknown) {
        trace.auth("CSRF token init failed", { error: String(error) }, "error");
        analytics.error("csrf_init_token_error", error, {
          forceRefresh: Boolean(options.forceRefresh),
          forceServerSync: Boolean(options.forceServerSync),
        });
      }
    }

    return token;
  })();

  try {
    return await csrfInitializationPromise;
  } finally {
    csrfInitializationPromise = null;
  }
}

/**
 * Ensure a token exists and that the server-side cookie is reasonably fresh.
 */
export async function ensureCsrfToken(options: InitializeCsrfOptions = {}): Promise<string> {
  return initializeCsrfToken(options);
}

/**
 * Add CSRF token to request headers.
 */
export function addCsrfHeader(headers: Headers): void {
  const token = getCsrfToken();
  if (token) {
    headers.set("X-CSRF-Token", token);
  }
}

/**
 * Middleware to add CSRF protection to state-changing requests.
 */
export function requiresCsrfToken(method: string): boolean {
  return ["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase());
}
